import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { Checkbox, FormControlLabel, Grid, Tooltip, Typography } from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { MuiTelInput } from 'mui-tel-input';
import { emptyArray, emptyString, emptyCity, applicationFeatureCodes } from 'constants/defaultValues';
import { get, set } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange, onTelInputChange, onTextBoxChange } from 'utils/propertyChangeAdapters';
import { valueOrDefault } from 'utils/functions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { OCRButton } from 'features/ocr';
import { useApplicationFeature } from 'hooks';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const PartnerComponent = ({
  partnerLens,
  insuranceTypeCode,
  cascoInsuranceTypeCode,
  partnerTypes,
  disabledStatus,
  activityTypes,
  usageTypes,
  counties,
  cities,
  citizenships,
  nationalities,
  defaultCountryCode,
  personDocumentTypes,
  isPF,
  hasRightToSearch,
  validation,
  CustomContent,
  loadingStatus,
  composeControlId,
  getUniqueIdLabel,
  onSave,
  onUniqueIdBlur,
  onContactPersonUniqueIdBlur,
  onUniqueIdChanged,
  onContactPersonUniqueIdChanged,
  onPartnerTypeChange,
  onDistrictChange,
  onOpenSearchPartnersModal,
  ocrContext
}) => {
  const { t } = useTranslation();
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditInsuredOrUser = useAuthorization(AuthorizationCodes.hasRightToEditInsuredOrUser, documentTypeId, documentStateId);
  const _isPF = isPF(partnerLens.partnerTypeId |> get);
  const fullAddress = partnerLens.fullAddress |> get;
  const enableOcrIntegration = useApplicationFeature(applicationFeatureCodes.ENABLE_OCR_INTEGRATION);

  const ExtendedContent = () => {
    return (
      <>
        {CustomContent && (
          <Grid item xs={12}>
            <CustomContent.BodyExtension />
          </Grid>
        )}
      </>
    );
  };

  return (
    <Grid container spacing={2} px={1}>
      <Grid item container spacing={2}>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <Typography fontWeight='bold' variant='subtitle1'>
              {t('Partner.ClientInfo')}
            </Typography>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={6} lg={6}>
              <Autocomplete
                id={composeControlId('PartnerType')}
                label={t('Partner.PartnerType')}
                fullWidth
                required
                isSearchable={true}
                isClearable={false}
                valueKey='id'
                labelKey='name'
                value={partnerLens.partnerTypeId |> get}
                onChange={onPartnerTypeChange}
                options={partnerTypes || emptyArray}
                simpleValue={true}
                disabled={
                  loadingStatus.partnerTypesLoading ||
                  disabledStatus.partnerTypes ||
                  !onPartnerTypeChange ||
                  disabledStatus.globalReadOnly({
                    hasAuthorization: hasRightToEditInsuredOrUser
                  })
                }
                error={!isValid(validation.partnerTypeId)}
                helperText={getErrors(validation.partnerTypeId)}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              {_isPF && (
                <FormControlLabel
                  control={
                    <Checkbox
                      id={composeControlId('GDPRConsent')}
                      checked={partnerLens.agreesWithPersonalDataUse |> get}
                      onChange={partnerLens.agreesWithPersonalDataUse |> set |> onCheckBoxChange}
                      disabled={
                        disabledStatus.agreesWithPersonalDataUse ||
                        disabledStatus.globalReadOnly({
                          hasAuthorization: hasRightToEditInsuredOrUser
                        })
                      }
                    />
                  }
                  label={t('Partner.GDPRConsent')}
                ></FormControlLabel>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <TextField
              id={composeControlId('UniqueId')}
              required
              fullWidth
              value={partnerLens.uniqueID |> get |> valueOrDefault(emptyString)}
              label={getUniqueIdLabel()}
              endAdornment={
                <>
                  {hasRightToSearch && (
                    <IconButton
                      id='partnerData_searchPartnerModalButton'
                      onClick={e => onOpenSearchPartnersModal(e, false)}
                      size='small'
                      variant='text'
                      color='primary'
                      tooltip={t('Partner.Search.SearchPartner')}
                      disabled={
                        disabledStatus.readonly ||
                        disabledStatus.globalReadOnly({
                          hasAuthorization: hasRightToEditInsuredOrUser
                        })
                      }
                    >
                      <SearchIcon />
                    </IconButton>
                  )}
                  {_isPF && ocrContext && enableOcrIntegration.enabled && (
                    <OCRButton
                      tooltip={t('Partner.Search.OCR')}
                      disabled={
                        disabledStatus.readonly ||
                        disabledStatus.globalReadOnly({
                          hasAuthorization: hasRightToEditInsuredOrUser
                        })
                      }
                      context={ocrContext}
                    />
                  )}
                </>
              }
              disabled={
                disabledStatus.readonly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              onChange={onTextBoxChange(onUniqueIdChanged, {
                rejectWhitespace: true
              })}
              error={!isValid(validation.uniqueID)}
              helperText={getErrors(validation.uniqueID)}
              onBlur={onUniqueIdBlur}
            />
          </Grid>
          {_isPF ? (
            <>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  id={composeControlId('FirstName')}
                  fullWidth
                  required
                  onChange={partnerLens.firstName |> set}
                  label={t('Partner.FirstName')}
                  value={partnerLens.firstName |> get |> valueOrDefault(emptyString)}
                  disabled={
                    disabledStatus.fullReadOnly ||
                    disabledStatus.globalReadOnly({
                      hasAuthorization: hasRightToEditInsuredOrUser
                    })
                  }
                  error={!isValid(validation.firstName)}
                  helperText={getErrors(validation.firstName)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  id={composeControlId('LastName')}
                  fullWidth
                  required
                  onChange={partnerLens.lastName |> set}
                  label={t('Partner.LastName')}
                  value={partnerLens.lastName |> get |> valueOrDefault(emptyString)}
                  disabled={
                    disabledStatus.fullReadOnly ||
                    disabledStatus.globalReadOnly({
                      hasAuthorization: hasRightToEditInsuredOrUser
                    })
                  }
                  error={!isValid(validation.lastName)}
                  helperText={getErrors(validation.lastName)}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                id={composeControlId('PartnerName')}
                fullWidth
                required
                onChange={partnerLens.partnerName |> set}
                label={t('Partner.Name')}
                value={partnerLens.partnerName |> get |> valueOrDefault(emptyString)}
                disabled={
                  disabledStatus.fullReadOnly ||
                  disabledStatus.globalReadOnly({
                    hasAuthorization: hasRightToEditInsuredOrUser
                  })
                }
                error={!isValid(validation.partnerName)}
                helperText={getErrors(validation.partnerName)}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={6}>
            <Autocomplete
              id={composeControlId('UsageType')}
              label={t('Partner.UsageType')}
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={partnerLens.usageTypeId |> get}
              onChange={partnerLens.usageTypeId |> set}
              options={usageTypes || emptyArray}
              simpleValue={true}
              disabled={
                loadingStatus.usageTypesLoading ||
                disabledStatus.readonly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.usageTypeId)}
              helperText={getErrors(validation.usageTypeId)}
            />
          </Grid>
          {!_isPF && (
            <>
              <Grid item xs={12} md={12} lg={6}>
                <Autocomplete
                  id={composeControlId('ActivityType')}
                  label={t('Partner.ActivityType')}
                  fullWidth
                  required
                  isSearchable={true}
                  isClearable={false}
                  valueKey='id'
                  labelKey='name'
                  value={partnerLens.activityFieldId |> get}
                  onChange={partnerLens.activityFieldId |> set}
                  options={activityTypes}
                  disabled={
                    loadingStatus.activityTypesLoading ||
                    disabledStatus.fullReadOnly ||
                    disabledStatus.globalReadOnly({
                      hasAuthorization: hasRightToEditInsuredOrUser
                    })
                  }
                  simpleValue={true}
                  error={!isValid(validation.activityFieldId)}
                  helperText={getErrors(validation.activityFieldId)}
                />
              </Grid>
            </>
          )}
          {_isPF && (
            <Grid item xs={12} md={6} lg={6}>
              <DateTime
                id={composeControlId('DrivingLicenseReleaseDate')}
                label={t('Partner.DrivingLicenseReleaseDate')}
                onChange={partnerLens.drivingLicenseReleaseDate |> set}
                value={partnerLens.drivingLicenseReleaseDate |> get}
                timeFormat={false}
                fullWidth
                required
                disabled={
                  disabledStatus.fullReadOnly ||
                  disabledStatus.globalReadOnly({
                    hasAuthorization: hasRightToEditInsuredOrUser
                  })
                }
                error={!isValid(validation.drivingLicenseReleaseDate)}
                helperText={getErrors(validation.drivingLicenseReleaseDate)}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <Typography fontWeight='bold' variant='subtitle1'>
            {t('Partner.Address')}
          </Typography>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={composeControlId('District')}
              label={t('Partner.District')}
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={partnerLens.districtId |> get}
              onChange={onDistrictChange ? value => onDistrictChange(value) : () => {}}
              options={counties || emptyArray}
              simpleValue={true}
              disabled={
                loadingStatus.countiesLoading ||
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.districtId)}
              helperText={getErrors(validation.districtId)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={composeControlId('City')}
              label={t('Partner.City')}
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={(partnerLens.city |> get) || emptyCity}
              onChange={partnerLens.city |> set}
              options={cities || emptyArray}
              simpleValue={false}
              disabled={
                loadingStatus.citiesLoading ||
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.city.id)}
              helperText={getErrors(validation.city.id)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('Street')}
              fullWidth
              required={insuranceTypeCode === cascoInsuranceTypeCode}
              onChange={partnerLens.street |> set}
              label={t('Partner.Street')}
              value={partnerLens.street |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.street)}
              helperText={getErrors(validation.street)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('StreetNumber')}
              fullWidth
              required={insuranceTypeCode === cascoInsuranceTypeCode}
              onChange={partnerLens.streetNumber |> set}
              label={t('Partner.StreetNumber')}
              value={partnerLens.streetNumber |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.streetNumber)}
              helperText={getErrors(validation.streetNumber)}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('BuildingNumber')}
              fullWidth
              onChange={partnerLens.buildingNumber |> set}
              label={t('Partner.BuildingNumber')}
              value={partnerLens.buildingNumber |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.buildingNumber)}
              helperText={getErrors(validation.buildingNumber)}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('StairNumber')}
              fullWidth
              onChange={partnerLens.stairNumber |> set}
              label={t('Partner.StairNumber')}
              value={partnerLens.stairNumber |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.stairNumber)}
              helperText={getErrors(validation.stairNumber)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('FloorNumber')}
              fullWidth
              onChange={partnerLens.floorNumber |> set}
              label={t('Partner.FloorNumber')}
              value={partnerLens.floorNumber |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.floorNumber)}
              helperText={getErrors(validation.floorNumber)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('FlatNumber')}
              fullWidth
              onChange={partnerLens.flatNumber |> set}
              label={t('Partner.FlatNumber')}
              value={partnerLens.flatNumber |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.floorNumber)}
              helperText={getErrors(validation.floorNumber)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('ZipCode')}
              fullWidth
              onChange={partnerLens.zipCode |> set}
              label={t('Partner.ZipCode')}
              value={partnerLens.zipCode |> get |> valueOrDefault(emptyString)}
              disabled={
                Boolean(disabledStatus.addressDisabled) ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                })
              }
              error={!isValid(validation.floorNumber)}
              helperText={getErrors(validation.floorNumber)}
            />
          </Grid>
          {fullAddress && (
            <Grid item xs={12} md={12} lg={6}>
              <TextField
                id={composeControlId('FullAddress')}
                fullWidth
                label={t('Partner.FullAddress')}
                value={fullAddress}
                disabled={true}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          {!_isPF ? (
            <Typography fontWeight='bold' variant='subtitle1'>
              {t('Partner.ContactPerson')}
            </Typography>
          ) : (
            <Typography fontWeight='bold' variant='subtitle1'>
              {t('Partner.PersonalData')}
            </Typography>
          )}
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <MuiTelInput
              id={composeControlId('PhoneNumber')}
              defaultCountry={defaultCountryCode}
              country={partnerLens.phoneCountryCode |> get}
              inputComponent={TextField}
              fullWidth
              required
              variant='standard'
              size='small'
              label={t('Partner.PhoneNumber')}
              value={partnerLens.phoneNumber |> get |> valueOrDefault(emptyString)}
              onChange={onTelInputChange(partnerLens.phoneNumber |> set, partnerLens.phoneCountryCode |> set)}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.phoneNumber)}
              helperText={getErrors(validation.phoneNumber)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('Email')}
              fullWidth
              required
              onChange={partnerLens.email |> set}
              label={t('Partner.Email')}
              value={partnerLens.email |> get |> valueOrDefault(emptyString)}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.email)}
              helperText={getErrors(validation.email)}
            />
          </Grid>
          {!_isPF && (
            <>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  id={composeControlId('ContactPersonUniqueID')}
                  fullWidth
                  onChange={onContactPersonUniqueIdChanged}
                  label={t('Partner.UniqueID.CNP')}
                  value={partnerLens.contactPersonUniqueID |> get |> valueOrDefault(emptyString)}
                  disabled={
                    disabledStatus.fullReadOnly ||
                    disabledStatus.globalReadOnly({
                      hasAuthorization: hasRightToEditInsuredOrUser
                    })
                  }
                  error={!isValid(validation.contactPersonUniqueID)}
                  helperText={getErrors(validation.contactPersonUniqueID)}
                  endAdornment={
                    hasRightToSearch && (
                      <IconButton
                        onClick={e => onOpenSearchPartnersModal(e, true)}
                        tooltip={t('Partner.Search.SearchPartner')}
                        variant='text'
                        size='small'
                        color='primary'
                        disabled={
                          disabledStatus.fullReadOnly ||
                          disabledStatus.globalReadOnly({
                            hasAuthorization: hasRightToEditInsuredOrUser
                          })
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    )
                  }
                  onBlur={onContactPersonUniqueIdBlur}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  id={composeControlId('ContactPersonName')}
                  fullWidth
                  onChange={partnerLens.contactPersonName |> set}
                  label={t('Partner.Name')}
                  value={partnerLens.contactPersonName |> get |> valueOrDefault(emptyString)}
                  disabled={
                    disabledStatus.fullReadOnly ||
                    disabledStatus.globalReadOnly({
                      hasAuthorization: hasRightToEditInsuredOrUser
                    }) ||
                    disabledStatus.contactPersonReadOnly
                  }
                  error={!isValid(validation.contactPersonName)}
                  helperText={getErrors(validation.contactPersonName)}
                  endAdornment={
                    <Tooltip title={t('Partner.Signer')}>
                      <BorderColorIcon />
                    </Tooltip>
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={composeControlId('Citizenship')}
              label={t('Partner.Citizenship')}
              fullWidth
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={partnerLens.citizenshipId |> get}
              onChange={partnerLens.citizenshipId |> set}
              options={citizenships || emptyArray}
              simpleValue={true}
              disabled={
                loadingStatus.citizenshipsLoading ||
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={composeControlId('Nationality')}
              label={t('Partner.Nationality')}
              fullWidth
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={partnerLens.nationalityId |> get}
              onChange={partnerLens.nationalityId |> set}
              options={nationalities || emptyArray}
              simpleValue={true}
              disabled={
                loadingStatus.nationalitiesLoading ||
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Autocomplete
              id={composeControlId('PersonDocumentType')}
              label={t('Partner.IdentityDocumentType')}
              fullWidth
              isSearchable={true}
              isClearable={false}
              valueKey='id'
              labelKey='name'
              value={partnerLens.identityDocumentTypeId |> get}
              onChange={partnerLens.identityDocumentTypeId |> set}
              options={personDocumentTypes || emptyArray}
              simpleValue={true}
              disabled={
                loadingStatus.personDocumentTypesLoading ||
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('IdentityCardNo')}
              fullWidth
              onChange={partnerLens.identityCardNo |> set}
              label={t('Partner.IdentityCardNo')}
              value={partnerLens.identityCardNo |> get |> valueOrDefault(emptyString)}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.identityCardNo)}
              helperText={getErrors(validation.identityCardNo)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              id={composeControlId('IssuedBy')}
              fullWidth
              onChange={partnerLens.issuedBy |> set}
              label={t('Partner.IssuedBy')}
              value={partnerLens.issuedBy |> get |> valueOrDefault(emptyString)}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.issuedBy)}
              helperText={getErrors(validation.issuedBy)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DateTime
              id={composeControlId('IssuedAt')}
              label={t('Partner.IssuedAt')}
              fullWidth
              onChange={partnerLens.identityDocumentIssueDate |> set}
              value={partnerLens.identityDocumentIssueDate |> get}
              timeFormat={false}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.identityDocumentIssueDate)}
              helperText={getErrors(validation.identityDocumentIssueDate)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <DateTime
              id={composeControlId('ValidUntil')}
              label={t('Partner.ValidUntil')}
              fullWidth
              onChange={partnerLens.identityDocumentExpireDate |> set}
              value={partnerLens.identityDocumentExpireDate |> get}
              timeFormat={false}
              disabled={
                disabledStatus.fullReadOnly ||
                disabledStatus.globalReadOnly({
                  hasAuthorization: hasRightToEditInsuredOrUser
                }) ||
                disabledStatus.contactPersonReadOnly
              }
              error={!isValid(validation.identityDocumentExpireDate)}
              helperText={getErrors(validation.identityDocumentExpireDate)}
            />
          </Grid>
        </Grid>
      </Grid>
      <ExtendedContent />
      <Grid item xs={12}>
        <Button
          id={composeControlId('Save')}
          color='primary'
          onClick={onSave}
          disabled={disabledStatus.saveDisabled || disabledStatus.globalReadOnly({ hasAuthorization: hasRightToEditInsuredOrUser })}
        >
          {disabledStatus.saving ? t('General.Saving') : t('General.Save')}
        </Button>
      </Grid>
    </Grid>
  );
};

PartnerComponent.propTypes = {
  partnerLens: PropTypes.object.isRequired,
  insuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  partnerTypes: PropTypes.array,
  disabledStatus: PropTypes.object.isRequired,
  activityTypes: PropTypes.array,
  usageTypes: PropTypes.array,
  counties: PropTypes.array,
  cities: PropTypes.array,
  citizenships: PropTypes.array,
  nationalities: PropTypes.array,
  defaultCountryCode: PropTypes.string,
  personDocumentTypes: PropTypes.array,
  isPF: PropTypes.func.isRequired,
  hasRightToSearch: PropTypes.bool,
  validation: PropTypes.object,
  CustomContent: PropTypes.object,
  loadingStatus: PropTypes.object.isRequired,
  composeControlId: PropTypes.func.isRequired,
  getUniqueIdLabel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func,
  onContactPersonUniqueIdBlur: PropTypes.func,
  onUniqueIdChanged: PropTypes.func,
  onContactPersonUniqueIdChanged: PropTypes.func,
  onPartnerTypeChange: PropTypes.func,
  onDistrictChange: PropTypes.func,
  onOpenSearchPartnersModal: PropTypes.func,
  ocrContext: PropTypes.object
};

export default PartnerComponent;
